import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { UtilityService } from '../utility.service';
import { BaseFormService } from '../BaseFormService';
import { BillingFormListView } from 'app/models/billing-forms/BillingFormListView';
import { BillingFormListFilter } from 'app/models/billing-forms/BillingFormListFilter';
import { BillingFormView } from 'app/models/billing-forms/BillingFormView';
import { BillingFormSubmissionView } from 'app/models/billing-forms/BillingFormSubmissionView';
import { BillingFormSubmissionAnalyticsView } from 'app/models/billing-forms/BillingFormSubmissionAnalyticsView';
import { BillingFormSubmissionListFilter } from 'app/models/billing-forms/BillingFormSubmissionListFilter';

export interface PaginatedResult<T> {
	entities: T[];
	totalRecords: number;
	page: number;
	pageSize: number;
}

export type BillingFormPage = PaginatedResult<BillingFormListView>;

export interface BillingFormSubmissionPage extends PaginatedResult<BillingFormSubmissionView> {
	analytics: BillingFormSubmissionAnalyticsView;
}

@Injectable({ providedIn: 'root' })
export class BillingFormService extends BaseFormService {
	constructor(httpClient: HttpClient, utilityService: UtilityService) {
		super(`${environment.apiUrl}billing/clients/$$clientId$$/forms`, httpClient, utilityService);
	}

	getForms(clientId: string, filter: BillingFormListFilter): Observable<BillingFormPage> {
		const params = this.utilityService.objectToQueryString(filter);
		return this.httpClient.get<BillingFormPage>(`${this.getUrl(clientId)}?${params}`);
	}

	duplicateForm(
		clientId: string,
		formId: string,
		request: { newName: string; slug: string }
	): Observable<BillingFormListView> {
		return this.httpClient.post<BillingFormListView>(`${this.getUrl(clientId)}/${formId}/duplicate`, request);
	}

	toggleActive(clientId: string, formId: string): Observable<void> {
		return this.httpClient.patch<void>(`${this.getUrl(clientId)}/${formId}/toggle-active`, {});
	}

	getForm(clientId: string, formId: string): Observable<BillingFormView> {
		return this.httpClient.get<BillingFormView>(`${this.getUrl(clientId)}/${formId}`);
	}

	createForm(clientId: string, request: BillingFormView): Observable<BillingFormView> {
		const formData = new FormData();
		formData.append('model', JSON.stringify(request));
		return this.httpClient.post<BillingFormView>(`${this.getUrl(clientId)}`, formData);
	}

	updateForm(clientId: string, formId: string, request: BillingFormView): Observable<BillingFormView> {
		const formData = new FormData();
		formData.append('model', JSON.stringify(request));
		return this.httpClient.put<BillingFormView>(`${this.getUrl(clientId)}/${formId}`, formData);
	}

	checkSlug(clientId: string, slug: string, excludeFormId?: string): Observable<{ available: boolean }> {
		let url = `${this.getUrl(clientId)}/slug/check?slug=${encodeURIComponent(slug)}`;
		if (excludeFormId) url += `&excludeFormId=${excludeFormId}`;
		return this.httpClient.get<{ available: boolean }>(url);
	}

	getSubmissions(
		clientId: string,
		formId: string,
		filter: BillingFormSubmissionListFilter
	): Observable<BillingFormSubmissionPage> {
		const params = this.utilityService.objectToQueryString(filter);
		return this.httpClient.get<BillingFormSubmissionPage>(
			`${this.getUrl(clientId)}/${formId}/submissions?${params}`
		);
	}

	getSubmission(clientId: string, formId: string, submissionId: string): Observable<BillingFormSubmissionView> {
		return this.httpClient.get<BillingFormSubmissionView>(
			`${this.getUrl(clientId)}/${formId}/submissions/${submissionId}`
		);
	}
}
